import request from '@/Network/Requests/request'
import Service from '@/Network/Services/Service'
import type {
  VacancyAllocatePayload,
  VacancyCenterQuery
} from '@/Network/Types/Requests/VacancyCenterRequests'
import type {
  VacancyCandidatesResponse,
  VacancyCenterResponse,
  VacancyItem,
  VacancyRemindResponse
} from '@/Network/Types/Responses/VacancyCenterResponses'

const QUERY_KEYS = [
  'days',
  'status',
  'healthUnits',
  'schedules',
  'sectorIds',
  'specialtyIds',
  'companySpecialtyIds',
  'withoutSector',
  'withoutSpecialty'
] as const satisfies readonly (keyof VacancyCenterQuery)[]

/** Só campos preenchidos sobem, como texto (listas já chegam em CSV). */
export const buildVacancyCenterParams = (query: VacancyCenterQuery): Record<string, string> => {
  const params: Record<string, string> = {}
  for (const key of QUERY_KEYS) {
    const value = query[key]
    if (value !== undefined && value !== null && value !== '') params[key] = String(value)
  }
  return params
}

/** Central de vagas (SPEC 2.5a §3): lista, compatíveis, alocar e cobrar confirmação. */
export default class VacancyCenterService extends Service {
  prefix: string

  constructor() {
    super()
    this.prefix = '/vacancy-center'
  }

  async list(query: VacancyCenterQuery): Promise<VacancyCenterResponse> {
    try {
      const { data } = await request.get<VacancyCenterResponse>(
        this.getCompletePath(),
        buildVacancyCenterParams(query)
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar a Central de vagas')
    }
  }

  async candidates(stationId: number, q?: string): Promise<VacancyCandidatesResponse> {
    const search = q?.trim()
    try {
      const { data } = await request.get<VacancyCandidatesResponse>(
        this.getCompletePath(`/stations/${stationId}/candidates`),
        search ? { q: search } : undefined
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os médicos compatíveis')
    }
  }

  /**
   * O `ServiceError` guarda o erro original: quem chama lê `errorCode`,
   * `reasons` e `violations` com `hasApiErrorCode`/`unwrapApiError`.
   */
  async allocate(stationId: number, body: VacancyAllocatePayload): Promise<VacancyItem> {
    try {
      const { data } = await request.post<VacancyItem>(
        this.getCompletePath(`/stations/${stationId}/allocate`),
        body
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível alocar o médico')
    }
  }

  async remind(stationId: number): Promise<VacancyRemindResponse> {
    try {
      const { data } = await request.post<VacancyRemindResponse>(
        this.getCompletePath(`/stations/${stationId}/remind`),
        {}
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível enviar o lembrete')
    }
  }
}
